"use client";

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  DEFAULT_APP_PREFERENCES,
  normalizeAppPreferences,
  type AppAccentColor,
  type AppReasoningStyle,
} from "@/lib/appPreferences";
import { unwrapApiData } from "@/lib/apiResponse";
import { fetchWithAuth } from "@/lib/fetchWithAuth";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000";
const REASONING_SAVE_RETRY_DELAYS_MS = [180, 600] as const;

type AccentPreferenceStatus = "loading" | "ready" | "error";

export type AppPreferencesContextValue = {
  accentColor: AppAccentColor;
  reasoningStyle: AppReasoningStyle;
  status: AccentPreferenceStatus;
  saving: boolean;
  saveError: boolean;
  reload: () => Promise<void>;
  saveAccentColor: (accentColor: AppAccentColor) => Promise<boolean>;
  saveReasoningStyle: (reasoningStyle: AppReasoningStyle) => Promise<boolean>;
};

const AccentColorContext = createContext<AppPreferencesContextValue | null>(
  null,
);

function applyAccentColor(accentColor: AppAccentColor): void {
  document.documentElement.dataset.accent = accentColor;
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

export function AccentColorProvider({ children }: { children: ReactNode }) {
  const [accentColor, setAccentColor] = useState<AppAccentColor>(
    DEFAULT_APP_PREFERENCES.accentColor,
  );
  const [reasoningStyle, setReasoningStyle] = useState<AppReasoningStyle>(
    DEFAULT_APP_PREFERENCES.reasoningStyle,
  );
  const [status, setStatus] = useState<AccentPreferenceStatus>("loading");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const mountedRef = useRef(false);
  const savingRef = useRef(false);
  const requestIdRef = useRef(0);

  // Reasoning-style persistence is intentionally independent from the blocking
  // accent-color save flow. UI selection is optimistic and immediate; this
  // queue only serializes background PATCH calls and guarantees last-write-wins.
  const reasoningDesiredRef = useRef<AppReasoningStyle>(
    DEFAULT_APP_PREFERENCES.reasoningStyle,
  );
  const reasoningPersistedRef = useRef<AppReasoningStyle>(
    DEFAULT_APP_PREFERENCES.reasoningStyle,
  );
  const reasoningRevisionRef = useRef(0);
  const reasoningSaveInFlightRef = useRef(false);
  const reasoningSaveLoopRef = useRef<Promise<boolean> | null>(null);

  const loadPreferences = useCallback(async (showLoading: boolean) => {
    const requestId = ++requestIdRef.current;
    const reasoningRevision = reasoningRevisionRef.current;
    if (showLoading) setStatus("loading");

    try {
      const response = await fetchWithAuth(
        `${API_URL}/api/users/me/app-preferences`,
        { skipRedirectOn401: true },
      );
      if (!response.ok) {
        throw new Error(`Preference request failed: ${response.status}`);
      }

      const preferences = normalizeAppPreferences(
        unwrapApiData(await response.json()),
      );
      if (!mountedRef.current || requestId !== requestIdRef.current) return;

      setAccentColor(preferences.accentColor);
      applyAccentColor(preferences.accentColor);

      const reasoningUnchangedSinceRequest =
        reasoningRevision === reasoningRevisionRef.current;
      const reasoningHasPendingWrite =
        reasoningSaveInFlightRef.current ||
        reasoningDesiredRef.current !== reasoningPersistedRef.current;
      if (reasoningUnchangedSinceRequest && !reasoningHasPendingWrite) {
        reasoningDesiredRef.current = preferences.reasoningStyle;
        reasoningPersistedRef.current = preferences.reasoningStyle;
        setReasoningStyle(preferences.reasoningStyle);
      }

      setSaveError(false);
      setStatus("ready");
    } catch {
      if (!mountedRef.current || requestId !== requestIdRef.current) return;

      setAccentColor(DEFAULT_APP_PREFERENCES.accentColor);
      applyAccentColor(DEFAULT_APP_PREFERENCES.accentColor);

      const reasoningUnchangedSinceRequest =
        reasoningRevision === reasoningRevisionRef.current;
      const reasoningHasPendingWrite =
        reasoningSaveInFlightRef.current ||
        reasoningDesiredRef.current !== reasoningPersistedRef.current;
      if (reasoningUnchangedSinceRequest && !reasoningHasPendingWrite) {
        reasoningDesiredRef.current = DEFAULT_APP_PREFERENCES.reasoningStyle;
        reasoningPersistedRef.current = DEFAULT_APP_PREFERENCES.reasoningStyle;
        setReasoningStyle(DEFAULT_APP_PREFERENCES.reasoningStyle);
      }
      setStatus("error");
    }
  }, []);

  const flushReasoningStyleQueue = useCallback((): Promise<boolean> => {
    const running = reasoningSaveLoopRef.current;
    if (running) return running;

    const loop = (async () => {
      reasoningSaveInFlightRef.current = true;
      let fullySaved = true;

      try {
        while (
          mountedRef.current &&
          reasoningPersistedRef.current !== reasoningDesiredRef.current
        ) {
          const target = reasoningDesiredRef.current;
          let saved = false;

          for (
            let attempt = 0;
            attempt <= REASONING_SAVE_RETRY_DELAYS_MS.length;
            attempt += 1
          ) {
            try {
              const response = await fetchWithAuth(
                `${API_URL}/api/users/me/app-preferences`,
                {
                  method: "PATCH",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({ reasoningStyle: target }),
                },
              );
              if (!response.ok) {
                throw new Error(
                  `Preference save failed: ${response.status}`,
                );
              }

              const preferences = normalizeAppPreferences(
                unwrapApiData(await response.json()),
              );
              if (preferences.reasoningStyle !== target) {
                throw new Error("Reasoning preference was not persisted");
              }

              reasoningPersistedRef.current = target;
              saved = true;
              if (mountedRef.current) {
                setSaveError(false);
                setStatus("ready");
              }
              break;
            } catch {
              // If the user already moved to another step, do not waste retries
              // on a stale target; the outer loop immediately persists latest.
              if (reasoningDesiredRef.current !== target) break;
              const retryDelay = REASONING_SAVE_RETRY_DELAYS_MS[attempt];
              if (retryDelay === undefined) break;
              await wait(retryDelay);
            }
          }

          if (!saved) {
            if (reasoningDesiredRef.current !== target) continue;
            fullySaved = false;
            if (mountedRef.current) setSaveError(true);
            break;
          }
        }

        return (
          fullySaved &&
          reasoningPersistedRef.current === reasoningDesiredRef.current
        );
      } finally {
        reasoningSaveInFlightRef.current = false;
        reasoningSaveLoopRef.current = null;
      }
    })();

    reasoningSaveLoopRef.current = loop;
    return loop;
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    applyAccentColor(DEFAULT_APP_PREFERENCES.accentColor);
    void loadPreferences(true);

    const syncPendingReasoningStyle = () => {
      if (
        reasoningDesiredRef.current !== reasoningPersistedRef.current &&
        !reasoningSaveInFlightRef.current
      ) {
        void flushReasoningStyleQueue();
      }
    };
    const handleResume = () => {
      if (reasoningDesiredRef.current !== reasoningPersistedRef.current) {
        syncPendingReasoningStyle();
        return;
      }
      if (!savingRef.current && !reasoningSaveInFlightRef.current) {
        void loadPreferences(false);
      }
    };

    // online도 LanguageProvider -> session gate -> seexr:resume 순서로 합쳐서
    // 세션 확인 전에 사용자 preference API가 먼저 호출되지 않게 한다.
    window.addEventListener("seexr:resume", handleResume);

    return () => {
      mountedRef.current = false;
      requestIdRef.current += 1;
      window.removeEventListener("seexr:resume", handleResume);
      applyAccentColor(DEFAULT_APP_PREFERENCES.accentColor);
    };
  }, [flushReasoningStyleQueue, loadPreferences]);

  const reload = useCallback(() => loadPreferences(true), [loadPreferences]);

  const saveAccentColor = useCallback(
    async (nextAccentColor: AppAccentColor): Promise<boolean> => {
      if (savingRef.current || nextAccentColor === accentColor) return true;

      const previousAccentColor = accentColor;
      const requestId = ++requestIdRef.current;
      savingRef.current = true;
      setSaving(true);
      setSaveError(false);
      setAccentColor(nextAccentColor);
      applyAccentColor(nextAccentColor);

      try {
        const response = await fetchWithAuth(
          `${API_URL}/api/users/me/app-preferences`,
          {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ accentColor: nextAccentColor }),
          },
        );
        if (!response.ok) {
          throw new Error(`Preference save failed: ${response.status}`);
        }

        const preferences = normalizeAppPreferences(
          unwrapApiData(await response.json()),
        );
        if (!mountedRef.current || requestId !== requestIdRef.current)
          return false;

        setAccentColor(preferences.accentColor);
        applyAccentColor(preferences.accentColor);
        setStatus("ready");
        return true;
      } catch {
        if (!mountedRef.current || requestId !== requestIdRef.current)
          return false;

        setAccentColor(previousAccentColor);
        applyAccentColor(previousAccentColor);
        setSaveError(true);
        return false;
      } finally {
        if (mountedRef.current && requestId === requestIdRef.current) {
          savingRef.current = false;
          setSaving(false);
        }
      }
    },
    [accentColor],
  );

  const saveReasoningStyle = useCallback(
    (nextReasoningStyle: AppReasoningStyle): Promise<boolean> => {
      if (nextReasoningStyle !== reasoningDesiredRef.current) {
        reasoningDesiredRef.current = nextReasoningStyle;
        reasoningRevisionRef.current += 1;
        setReasoningStyle(nextReasoningStyle);
        setSaveError(false);
      }

      if (reasoningPersistedRef.current === nextReasoningStyle) {
        return Promise.resolve(true);
      }
      return flushReasoningStyleQueue();
    },
    [flushReasoningStyleQueue],
  );

  return (
    <AccentColorContext.Provider
      value={{
        accentColor,
        reasoningStyle,
        status,
        saving,
        saveError,
        reload,
        saveAccentColor,
        saveReasoningStyle,
      }}
    >
      {children}
    </AccentColorContext.Provider>
  );
}

export function useAppPreferences(): AppPreferencesContextValue {
  const context = useContext(AccentColorContext);
  if (!context) {
    throw new Error(
      "useAppPreferences must be used within AccentColorProvider",
    );
  }
  return context;
}

export function useAccentColor(): AppPreferencesContextValue {
  return useAppPreferences();
}
